"use client";

import { c as _c } from "react/compiler-runtime";
import { CodeIcon, EyeIcon, ScrollTextIcon, TrashIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CreateSkillDialog } from "@/components/playground/create-skill-dialog";
import { GenerateSkillDialog } from "@/components/playground/generate-skill-dialog";
import { SkillsHeader } from "@/components/playground/skills-header";
import { SkillsSidebar } from "@/components/playground/skills-sidebar";
import { UploadSkillDialog } from "@/components/playground/upload-skill-dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { SKILL_DESCRIPTION_MAX, SKILL_NAME_MAX, useDeleteSkill, useSkills, useUpdateSkill } from "@/hooks/useSkills";
import { cn } from "@/lib/utils";
import type { Skill } from "@/hooks/useSkills";
import type { Organization } from "@/lib/types";
interface SkillsPageClientProps {
  selectedOrganization: Organization | null;
  initialSkillId: string | null;
}
export default function SkillsPageClient(t0) {
  const $ = _c(61);
  const {
    selectedOrganization,
    initialSkillId
  } = t0;
  const router = useRouter();
  const {
    data,
    isLoading
  } = useSkills();
  let t1;
  if ($[0] !== data?.skills) {
    t1 = data?.skills as Skill[] | undefined ?? [];
    $[0] = data?.skills;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const skills = t1;
  const [selectedSkillId, setSelectedSkillId] = useState(initialSkillId);
  let t2;
  let t3;
  if ($[2] !== initialSkillId) {
    t2 = () => {
      setSelectedSkillId(initialSkillId);
    };
    t3 = [initialSkillId];
    $[2] = initialSkillId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const [createOpen, setCreateOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewMode, setViewMode] = useState("rendered");
  const [skillToDelete, setSkillToDelete] = useState(null);
  const deleteSkill = useDeleteSkill();
  let t4;
  if ($[5] !== selectedSkillId || $[6] !== skills) {
    t4 = skills.find(s => s.id === selectedSkillId) ?? null;
    $[5] = selectedSkillId;
    $[6] = skills;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selectedSkill = t4;
  let t5;
  if ($[8] !== router) {
    t5 = skillId => {
      setSelectedSkillId(skillId);
      setViewMode("rendered");
      router.replace(`/skills?id=${skillId}`);
    };
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSelectSkill = t5;
  let t6;
  if ($[10] !== deleteSkill || $[11] !== router || $[12] !== selectedSkillId || $[13] !== skillToDelete) {
    t6 = () => {
      if (!skillToDelete) {
        return;
      }
      deleteSkill.mutate({
        params: {
          path: {
            id: skillToDelete.id
          }
        }
      }, {
        onSuccess: () => {
          if (selectedSkillId === skillToDelete.id) {
            setSelectedSkillId(null);
            router.replace("/skills");
          }
          setSkillToDelete(null);
        }
      });
    };
    $[10] = deleteSkill;
    $[11] = router;
    $[12] = selectedSkillId;
    $[13] = skillToDelete;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleDeleteConfirm = t6;
  let t7;
  if ($[15] !== router) {
    t7 = skill => {
      setSelectedSkillId(skill.id);
      router.replace(`/skills?id=${skill.id}`);
    };
    $[15] = router;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleCreateSuccess = t7;
  let t10;
  let t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setCreateOpen(true);
    t9 = () => setGenerateOpen(true);
    t10 = () => setUploadOpen(true);
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== handleSelectSkill || $[21] !== isLoading || $[22] !== selectedOrganization || $[23] !== selectedSkillId || $[24] !== skills) {
    t11 = <SkillsSidebar skills={skills} selectedSkillId={selectedSkillId} onSelectSkill={handleSelectSkill} isLoading={isLoading} onCreateOpen={t8} onGenerateOpen={t9} onUploadOpen={t10} selectedOrganization={selectedOrganization} />;
    $[20] = handleSelectSkill;
    $[21] = isLoading;
    $[22] = selectedOrganization;
    $[23] = selectedSkillId;
    $[24] = skills;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SkillsHeader />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== selectedSkill || $[28] !== viewMode) {
    t13 = <div className="flex h-svh bg-background w-full overflow-hidden flex-col">{t12}<main className="flex flex-1 min-h-0 overflow-hidden"><SkillsPanel selectedSkill={selectedSkill} viewMode={viewMode} onViewModeChange={setViewMode} onDelete={setSkillToDelete} /></main></div>;
    $[27] = selectedSkill;
    $[28] = viewMode;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== createOpen || $[31] !== handleCreateSuccess) {
    t14 = <CreateSkillDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={handleCreateSuccess} />;
    $[30] = createOpen;
    $[31] = handleCreateSuccess;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== generateOpen || $[34] !== handleCreateSuccess) {
    t15 = <GenerateSkillDialog open={generateOpen} onOpenChange={setGenerateOpen} onSuccess={handleCreateSuccess} />;
    $[33] = generateOpen;
    $[34] = handleCreateSuccess;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== handleCreateSuccess || $[37] !== uploadOpen) {
    t16 = <UploadSkillDialog open={uploadOpen} onOpenChange={setUploadOpen} onSuccess={handleCreateSuccess} />;
    $[36] = handleCreateSuccess;
    $[37] = uploadOpen;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = !!skillToDelete;
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = open => {
      if (!open) {
        setSkillToDelete(null);
      }
    };
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <AlertDialogTitle>Delete skill</AlertDialogTitle>;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = skillToDelete?.name;
  let t21;
  if ($[41] !== t20) {
    t21 = <AlertDialogHeader>{t19}<AlertDialogDescription>Are you sure you want to delete "{t20}"? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <AlertDialogCancel>Cancel</AlertDialogCancel>;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = deleteSkill.isPending ? "Deleting..." : "Delete";
  let t24;
  if ($[44] !== deleteSkill.isPending || $[45] !== handleDeleteConfirm || $[46] !== t23) {
    t24 = <AlertDialogFooter>{t22}<AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={handleDeleteConfirm} disabled={deleteSkill.isPending}>{t23}</AlertDialogAction></AlertDialogFooter>;
    $[44] = deleteSkill.isPending;
    $[45] = handleDeleteConfirm;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t21 || $[49] !== t24) {
    t25 = <AlertDialogContent>{t21}{t24}</AlertDialogContent>;
    $[48] = t21;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t17 || $[52] !== t25) {
    t26 = <AlertDialog open={t17} onOpenChange={t18}>{t25}</AlertDialog>;
    $[51] = t17;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t11 || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== t16 || $[59] !== t26) {
    t27 = <SidebarProvider>{t11}{t13}{t14}{t15}{t16}{t26}</SidebarProvider>;
    $[54] = t11;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
    $[58] = t16;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}
interface SkillsPanelProps {
  selectedSkill: Skill | null;
  viewMode: "rendered" | "raw";
  onViewModeChange: (mode: "rendered" | "raw") => void;
  onDelete: (skill: Skill) => void;
}
function SkillsPanel(t0) {
  const $ = _c(33);
  const {
    selectedSkill,
    viewMode,
    onViewModeChange,
    onDelete
  } = t0;
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editInstructions, setEditInstructions] = useState("");
  const updateSkill = useUpdateSkill();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsEditing(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = selectedSkill?.id;
  let t3;
  if ($[1] !== t2) {
    t3 = [t2];
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useEffect(t1, t3);
  let t4;
  if ($[3] !== selectedSkill) {
    t4 = () => {
      if (!selectedSkill) {
        return;
      }
      setEditName(selectedSkill.name);
      setEditDescription(selectedSkill.description ?? "");
      setEditInstructions(selectedSkill.instructions);
      setIsEditing(true);
    };
    $[3] = selectedSkill;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const startEdit = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsEditing(false);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const cancelEdit = t5;
  let t6;
  if ($[6] !== editDescription || $[7] !== editInstructions || $[8] !== editName || $[9] !== selectedSkill || $[10] !== updateSkill) {
    t6 = async () => {
      if (!selectedSkill || !editName.trim() || !editInstructions.trim()) {
        return;
      }
      try {
        await updateSkill.mutateAsync({
          params: {
            path: {
              id: selectedSkill.id
            }
          },
          body: {
            name: editName.trim(),
            description: editDescription.trim(),
            instructions: editInstructions.trim()
          }
        });
        setIsEditing(false);
      } catch {}
    };
    $[6] = editDescription;
    $[7] = editInstructions;
    $[8] = editName;
    $[9] = selectedSkill;
    $[10] = updateSkill;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleSave = t6;
  const editNameTooLong = editName.length > SKILL_NAME_MAX;
  const editDescriptionTooLong = editDescription.length > SKILL_DESCRIPTION_MAX;
  let t7;
  if ($[12] !== editDescriptionTooLong || $[13] !== editInstructions || $[14] !== editName || $[15] !== editNameTooLong || $[16] !== updateSkill) {
    t7 = !editName.trim() || !editInstructions.trim() || editNameTooLong || editDescriptionTooLong || updateSkill.isPending;
    $[12] = editDescriptionTooLong;
    $[13] = editInstructions;
    $[14] = editName;
    $[15] = editNameTooLong;
    $[16] = updateSkill;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const isSaveDisabled = t7;
  let t8;
  if ($[18] !== editDescription || $[19] !== editDescriptionTooLong || $[20] !== editInstructions || $[21] !== editName || $[22] !== editNameTooLong || $[23] !== handleSave || $[24] !== isEditing || $[25] !== isSaveDisabled || $[26] !== onDelete || $[27] !== onViewModeChange || $[28] !== selectedSkill || $[29] !== startEdit || $[30] !== updateSkill || $[31] !== viewMode) {
    t8 = <div className="flex flex-1 min-h-0 flex-col overflow-hidden">{!selectedSkill ? <div className="flex h-full items-center justify-center px-6 text-center"><div><ScrollTextIcon className="mx-auto mb-4 h-10 w-10 text-muted-foreground/40" /><p className="text-muted-foreground text-sm">Select a skill to view details</p></div></div> : isEditing ? <><div className="flex items-center justify-between border-b px-6 py-4"><h2 className="text-lg font-semibold">Edit skill</h2><div className="flex items-center gap-2"><Button variant="ghost" size="sm" onClick={cancelEdit}>Cancel</Button><Button size="sm" onClick={handleSave} disabled={isSaveDisabled}>{updateSkill.isPending ? "Saving..." : "Save"}</Button></div></div><div className="flex-1 overflow-y-auto p-6 space-y-4"><div className="space-y-2"><div className="flex items-center justify-between"><Label htmlFor="inline-edit-name">Name</Label><span className={cn("text-xs tabular-nums", editNameTooLong ? "text-destructive" : "text-muted-foreground")}>{editName.length}/{SKILL_NAME_MAX}</span></div><Input id="inline-edit-name" value={editName} onChange={e => setEditName(e.target.value)} placeholder="brand-guidelines" aria-invalid={editNameTooLong || undefined} /></div><div className="space-y-2"><div className="flex items-center justify-between"><Label htmlFor="inline-edit-description">Description</Label><span className={cn("text-xs tabular-nums", editDescriptionTooLong ? "text-destructive" : "text-muted-foreground")}>{editDescription.length}/{SKILL_DESCRIPTION_MAX}</span></div><Textarea id="inline-edit-description" value={editDescription} onChange={e_0 => setEditDescription(e_0.target.value)} placeholder="Describe when to use this skill..." rows={2} aria-invalid={editDescriptionTooLong || undefined} /></div><div className="space-y-2"><Label htmlFor="inline-edit-instructions">Instructions</Label><Textarea id="inline-edit-instructions" value={editInstructions} onChange={e_1 => setEditInstructions(e_1.target.value)} placeholder="Enter your skill instructions in markdown..." rows={14} className="font-mono text-sm" /></div></div></> : <><div className="flex items-center justify-between px-6 py-4"><div className="min-w-0 flex-1"><h2 className="truncate text-lg font-semibold">{selectedSkill.name}</h2>{selectedSkill.description && <p className="mt-0.5 text-sm text-muted-foreground">{selectedSkill.description}</p>}</div><div className="ml-4 flex shrink-0 items-center gap-2"><Button variant="ghost" size="sm" onClick={startEdit}>Edit</Button><Button variant="ghost" size="icon" className="text-destructive h-8 w-8" onClick={() => onDelete(selectedSkill)} aria-label="Delete skill"><TrashIcon className="h-4 w-4" /></Button></div></div><div className="relative flex items-center px-6"><Separator /><div className="absolute right-6 bg-background pl-2"><Tabs value={viewMode} onValueChange={v => onViewModeChange(v as "rendered" | "raw")}><TabsList className="h-7"><TabsTrigger value="rendered" className="h-6 w-6 p-0" aria-label="Rendered view"><EyeIcon className="h-3.5 w-3.5" /></TabsTrigger><TabsTrigger value="raw" className="h-6 w-6 p-0" aria-label="Raw view"><CodeIcon className="h-3.5 w-3.5" /></TabsTrigger></TabsList></Tabs></div></div><div className="flex-1 overflow-y-auto">{viewMode === "rendered" ? <div className="prose prose-sm dark:prose-invert max-w-none p-6"><ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedSkill.instructions}</ReactMarkdown></div> : <pre className="h-full overflow-auto whitespace-pre-wrap break-words p-6 font-mono text-sm leading-relaxed">{selectedSkill.instructions}</pre>}</div></>}</div>;
    $[18] = editDescription;
    $[19] = editDescriptionTooLong;
    $[20] = editInstructions;
    $[21] = editName;
    $[22] = editNameTooLong;
    $[23] = handleSave;
    $[24] = isEditing;
    $[25] = isSaveDisabled;
    $[26] = onDelete;
    $[27] = onViewModeChange;
    $[28] = selectedSkill;
    $[29] = startEdit;
    $[30] = updateSkill;
    $[31] = viewMode;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  return t8;
}